import { isEmpty } from "lodash";
import { ComponentPropsWithoutRef } from "react";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/common/form";
import { Input } from "../../../ui/common/input";

interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<ComponentPropsWithoutRef<typeof Input>, "name"> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string | null;
  required?: boolean;
  description?: string;
  errorMessage?: string;
  /** Extra classes merged onto the label — every other caller keeps the default `FormLabel` size unless it passes this. */
  labelClassName?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
}

export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  description,
  errorMessage,
  labelClassName,
  rules,
  onBlur,
  ...inputProps
}: FormInputProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: required ? errorMessage || "This field is required" : false,
        ...rules
      }}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel className={labelClassName}>
            {label}
            {required && !isEmpty(label) && <span className="tw-text-destructive tw-ml-0.5">*</span>}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              {...inputProps}
              onBlur={event => {
                field.onBlur();
                onBlur?.(event);
              }}
            />
          </FormControl>
          {description && <p className="tw-text-sm tw-text-muted-foreground">{description}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
